/**
 * The two hooks every screen uses: `useLocale()` to read or change the language, and
 * `useMessages(namespace)` to turn a catalog key into a rendered string.
 *
 * ICU MessageFormat via `intl-messageformat` — plurals, selects and number/date formatting
 * are the translator's job, not the component's. Formatters are compiled once per
 * (locale, namespace, key) and cached; compiling an ICU pattern on every render is the
 * usual way this library becomes the slow part of a page.
 */

import IntlMessageFormat from 'intl-messageformat';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import type { MessageKeys, Namespace } from './catalog';
import { loadPack, packVersion, resolveCatalog, subscribePacks } from './catalog';
import type { Locale } from './locales';
import { LOCALE_LABELS, SUPPORTED_LOCALES } from './locales';
import { useLocaleStore } from './store';

/** Values a message placeholder can take. Deliberately narrow — no React nodes. */
export type MessageValues = Record<string, string | number | Date>;

const formatters = new Map<string, IntlMessageFormat>();

function formatterFor(locale: Locale, namespace: string, key: string, pattern: string) {
  const cacheKey = `${locale} ${namespace} ${key}`;
  const cached = formatters.get(cacheKey);
  if (cached !== undefined) return cached;
  const created = new IntlMessageFormat(pattern, locale);
  formatters.set(cacheKey, created);
  return created;
}

/**
 * The resolved catalog, re-resolved when its language's pack arrives.
 *
 * The store only ever points at a loaded language (`store.ts`), so for `useMessages` the
 * subscription is a formality that costs one integer read. It is load-bearing for
 * `useMessagesIn`, whose caller names a language of its own: the share page resolves the
 * recipient's from the browser, nothing has fetched that pack, and this is what fetches it and
 * turns the English the first render had to show into the translation once it is here.
 */
function useResolvedCatalog<N extends Namespace>(namespace: N, locale: Locale) {
  const version = useSyncExternalStore(subscribePacks, packVersion, packVersion);
  useEffect(() => {
    void loadPack(locale).catch(() => undefined);
  }, [locale]);
  return useMemo(() => {
    // Read for its change rather than its value: the resolver consults the pack table, which
    // React cannot see, and the version bump is the only signal that the table has a new row.
    void version;
    return resolveCatalog(namespace, locale);
  }, [namespace, locale, version]);
}

export interface LocaleControls {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
  readonly locales: readonly Locale[];
  readonly labels: Readonly<Record<Locale, string>>;
}

export function useLocale(): LocaleControls {
  const locale = useLocaleStore((state) => state.locale);
  const setLocale = useLocaleStore((state) => state.setLocale);
  return { locale, setLocale, locales: SUPPORTED_LOCALES, labels: LOCALE_LABELS };
}

/**
 * The same lookup, for a key the build cannot know.
 *
 * Almost every message in this app is named by a screen, which is why `Translate` is keyed by
 * the English catalog and a typo is a compile error. A **descriptor-driven** surface is the
 * exception: `KLQ-20260820-005` §6's axis dropdown is built from what the server says it
 * filters on, so the set of fields — and therefore the set of keys — is a property of the
 * running server rather than of this bundle.
 *
 * The fallback is the point rather than a concession. A new axis appears in the rail named by
 * its own wire field, which is legible enough to use and obviously unfinished enough that
 * somebody writes copy for it. The alternative — refusing to draw an axis with no key — hides
 * a working filter behind a missing string.
 */
export type TranslateLoosely = (key: string, fallback: string) => string;

export type Translate<N extends Namespace> = (
  key: MessageKeys[N],
  values?: MessageValues,
) => string;

/**
 * `t` for one namespace. The key type comes from the English catalog, so a typo is a
 * compile error and a deleted message breaks the build at every call site that used it.
 */
export function useMessages<N extends Namespace>(namespace: N): Translate<N> {
  const locale = useLocaleStore((state) => state.locale);
  return useMessagesIn(namespace, locale);
}

/**
 * The same `t`, in a language the caller names instead of the one the switch holds.
 *
 * One surface needs this and it is not a preference: the share page is read by somebody with
 * no account, so it resolves its own language from the browser and the sending project
 * (`KLQ-20260826-002` §F) and must not write that choice into the switch — a recipient's
 * browser is not a place this product gets to keep a preference. Everything else calls
 * `useMessages`, which is this function reading the store.
 */
/**
 * `t` for keys this bundle may not have. See `TranslateLoosely`.
 *
 * Deliberately **not** a second way to reach an ordinary message: it takes the fallback as an
 * argument, so a call site that wanted a catalog key and mistyped it renders the fallback
 * rather than the key, and the fallback is right there in the diff to be read.
 */
export function useLooseMessages(namespace: Namespace): TranslateLoosely {
  const locale = useLocaleStore((state) => state.locale);
  const resolved = useResolvedCatalog(namespace, locale);

  return useCallback<TranslateLoosely>(
    (key, fallback) => {
      const message = (resolved as Record<string, { pattern: string; locale: Locale }>)[key];
      if (message === undefined) return fallback;
      const formatted = formatterFor(
        message.locale,
        namespace,
        key,
        message.pattern,
      ).format();
      return typeof formatted === 'string' ? formatted : String(formatted);
    },
    [namespace, resolved],
  );
}

export function useMessagesIn<N extends Namespace>(
  namespace: N,
  locale: Locale,
): Translate<N> {
  const resolved = useResolvedCatalog(namespace, locale);

  return useCallback<Translate<N>>(
    (key, values) => {
      // `message.locale` is the language this *key* is written in, which is not always the
      // one that was asked for: a pack translated up to nine of twelve keys resolves the
      // other three to English (`catalog.ts`). ICU plural and select rules follow the text,
      // never the preference.
      const message = resolved[key];
      const formatted = formatterFor(
        message.locale,
        namespace,
        key,
        message.pattern,
      ).format(values);
      // ICU can return a part array when a message embeds rich text; the entrance catalog
      // has none, and joining is the honest degradation rather than a cast.
      return typeof formatted === 'string' ? formatted : String(formatted);
    },
    [namespace, resolved],
  );
}
